import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, PlayCircle, PauseCircle, CheckCircle, ArrowRight, X, Minus, MapPin, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { PieChartTooltip } from "@/components/ui/chart-tooltip";
import { listObras, type ObraRecord } from "@/lib/obraCache";
import { getClienteNomeById } from "@/utils/clientesData";

type StatusGroup = 'em-andamento' | 'paralisadas' | 'concluidas';

// Agrupa o statusObra persistido nas 3 categorias do card
function statusGroup(obra: ObraRecord): StatusGroup {
  switch (obra.statusObra) {
    case "concluido":
      return "concluidas";
    case "pausado":
    case "cancelado":
      return "paralisadas";
    // "em-andamento", "planejamento" ou vazio contam como ativas
    default:
      return "em-andamento";
  }
}

export const WorksSummary = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [expandedStatus, setExpandedStatus] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fonte única: obras reais (obraCache hidratado do Supabase)
  const obras = listObras();
  const grouped = useMemo(() => {
    const groups: Record<StatusGroup, ObraRecord[]> = {
      'em-andamento': [],
      'paralisadas': [],
      'concluidas': [],
    };
    for (const obra of obras) groups[statusGroup(obra)].push(obra);
    return groups;
  }, [obras]);

  const totalWorks = obras.length;
  const counts = {
    'em-andamento': grouped['em-andamento'].length,
    'paralisadas': grouped['paralisadas'].length,
    'concluidas': grouped['concluidas'].length,
  };

  const worksData = [
    { name: 'Em Andamento', value: counts['em-andamento'], color: 'hsl(var(--primary))' },
    { name: 'Paralisadas', value: counts['paralisadas'], color: 'hsl(var(--warning))' },
    { name: 'Concluídas', value: counts['concluidas'], color: 'hsl(var(--success))' }
  ].map(item => ({
    ...item,
    percentage: ((item.value / (totalWorks || 1)) * 100).toFixed(1),
  }));

  const handleStatusClick = (status: string) => {
    setExpandedStatus(expandedStatus === status ? null : status);
  };

  const handleViewAllWorks = () => {
    navigate('/obras');
  };

  if (!isVisible) return null;

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-foreground">
            <Building2 className="h-5 w-5 text-primary" />
            Resumo das Minhas Obras
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0 sm:h-6 sm:w-6"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <ChevronDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0 sm:h-6 sm:w-6"
              onClick={() => setIsVisible(false)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      {!isMinimized && (
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Summary Stats */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{totalWorks}</div>
                  <div className="text-sm text-muted-foreground">Total de Obras</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-success">{counts['em-andamento']}</div>
                  <div className="text-sm text-muted-foreground">Ativas</div>
                </div>
              </div>

              <div className="space-y-1">
                <button 
                  onClick={() => handleStatusClick('em-andamento')}
                  className="w-full flex items-center justify-between hover:bg-accent/50 rounded-md p-2 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <PlayCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">Em Andamento</span>
                  </div>
                  <span className="font-semibold">{counts['em-andamento']}</span>
                </button>
                
                <button 
                  onClick={() => handleStatusClick('paralisadas')}
                  className="w-full flex items-center justify-between hover:bg-accent/50 rounded-md p-2 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <PauseCircle className="h-4 w-4 text-warning" />
                    <span className="text-sm">Paralisadas</span>
                  </div>
                  <span className="font-semibold">{counts['paralisadas']}</span>
                </button>
                
                <button 
                  onClick={() => handleStatusClick('concluidas')}
                  className="w-full flex items-center justify-between hover:bg-accent/50 rounded-md p-2 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success" />
                    <span className="text-sm">Concluídas</span>
                  </div>
                  <span className="font-semibold">{counts['concluidas']}</span>
                </button>
              </div>

              {/* Expanded Works List */}
              {expandedStatus && (
                <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium capitalize">{expandedStatus.replace('-', ' ')}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={() => setExpandedStatus(null)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(grouped[expandedStatus as StatusGroup] ?? [])
                      .slice()
                      .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))
                      .slice(0, 3)
                      .map((obra) => (
                      <div
                        key={obra.id}
                        className="flex items-center justify-between p-2 bg-background rounded border text-xs cursor-pointer hover:bg-accent/50"
                        onClick={() => navigate(`/dashboard?obra=${obra.id}`)}
                      >
                        <div className="flex-1">
                          <div className="font-medium">{obra.nomeObra || "Obra sem nome"}</div>
                          <div className="text-muted-foreground flex items-center gap-1">
                            <span>{obra.clienteObra ? getClienteNomeById(obra.clienteObra) : "Cliente não informado"}</span>
                            <span>•</span>
                            <MapPin className="h-3 w-3" />
                            <span>{obra.endereco?.cidade || "Cidade não informada"}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(grouped[expandedStatus as StatusGroup] ?? []).length === 0 && (
                      <div className="text-xs text-muted-foreground p-2">
                        Nenhuma obra nesta categoria.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button className="w-full mt-4" variant="outline" onClick={handleViewAllWorks}>
                Ver todas as obras
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            {/* Chart */}
            <div className="h-64">
              {totalWorks === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
                  <Building2 className="h-8 w-8 opacity-50" />
                  <span>Nenhuma obra cadastrada ainda.</span>
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={worksData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {worksData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieChartTooltip />} />
                    <Legend />
                  </PieChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};